import { useEffect, useRef } from "react";

const MARQUEE_ITEMS = [
	"React",
	"Node.js",
	"TypeScript",
	"AWS",
	"LLM Integrations",
	"Agentic Workflows",
	"PostgreSQL",
	"Docker · Kubernetes",
	"TensorFlow",
	"Python",
	"GraphQL",
	"CI/CD",
];

const SKILLS = [
	{
		icon: "⬡",
		name: "Frontend Systems",
		desc: "Crafting high-performance, accessible UIs with modern React architecture, design systems, and component-driven development.",
		tags: ["React", "TypeScript", "Next.js", "CSS"],
	},
	{
		icon: "◈",
		name: "Backend & APIs",
		desc: "Designing scalable REST and GraphQL APIs, microservices architectures, and cloud-native backends that handle real load.",
		tags: ["Node.js", "Express", "GraphQL", "PostgreSQL"],
	},
	{
		icon: "◫",
		name: "Cloud Infrastructure",
		desc: "Architecting and operating cloud-native systems on AWS with infrastructure-as-code, CI/CD pipelines, and container orchestration.",
		tags: ["AWS", "Docker", "Kubernetes", "Terraform"],
	},
	{
		icon: "◎",
		name: "AI & ML Engineering",
		desc: "Building LLM-powered products, agentic workflows, RAG pipelines, and ML model integrations at the application layer.",
		tags: ["LLM APIs", "TensorFlow", "RAG", "Vector DBs"],
	},
	{
		icon: "◇",
		name: "Data Engineering",
		desc: "Designing data pipelines, analytics platforms, and real-time processing systems that turn raw data into product intelligence.",
		tags: ["Python", "SQL", "ETL", "Pandas"],
	},
	{
		icon: "◉",
		name: "Team Leadership",
		desc: "Mentoring engineers, leading technical planning, driving code quality culture, and aligning engineering execution with product goals.",
		tags: ["Mentoring", "Architecture", "Agile", "Code Review"],
	},
];

const EXPERIENCE = [
	{
		date: "2022 — Present",
		company: "CoBank",
		role: "Senior Fullstack Engineer",
		desc: "Led development of cloud-native banking applications. Architected React + Node.js platform serving thousands of agricultural and infrastructure loan customers across the US.",
	},
	{
		date: "2021 — 2022",
		company: "Tipico",
		role: "Senior Fullstack Engineer",
		desc: "Built real-time sports betting interfaces and backend services. Worked on high-availability, low-latency systems handling live odds feeds and wagering transactions.",
	},
	{
		date: "2020 — 2021",
		company: "Brinks Home Security",
		role: "Fullstack Engineer",
		desc: "Developed customer-facing web applications and internal tools supporting home security monitoring and account management platforms.",
	},
	{
		date: "2019 — 2020",
		company: "TransUnion",
		role: "Software Engineer",
		desc: "Contributed to consumer credit data products and API integrations. Worked on compliance-sensitive data pipelines serving major financial institutions.",
	},
	{
		date: "2017 — 2019",
		company: "Egencia (Expedia Group)",
		role: "Software Engineer",
		desc: "Built travel booking and expense management features for corporate clients worldwide. Worked in a microservices ecosystem with high throughput and global distribution.",
	},
	{
		date: "2016 — 2017",
		company: "Accenture · Relativity",
		role: "Software Engineer",
		desc: "Early-career roles across consulting and legal tech. Developed enterprise application features and client-facing integrations in fast-paced agile teams.",
	},
];

const CERTS = [
	{ name: "Machine Learning Specialization", issuer: "DeepLearning.AI" },
	{ name: "AI Engineering Professional", issuer: "IBM" },
	{ name: "Full Stack Development", issuer: "IBM" },
	{ name: "Data Science Professional", issuer: "IBM" },
	{ name: "Deep Learning Specialization", issuer: "DeepLearning.AI" },
];

export default function App() {
	const cursorRef = useRef<HTMLDivElement>(null);
	const ringRef = useRef<HTMLDivElement>(null);

	// Custom cursor
	useEffect(() => {
		if (!window.matchMedia("(pointer: fine)").matches) {
			return;
		}

		let mx = 0;
		let my = 0;
		let rx = 0;
		let ry = 0;
		let rafId: number | undefined;
		let isAnimating = false;
		let hasPointerPosition = false;

		const startAnimation = () => {
			if (!isAnimating) {
				isAnimating = true;
				rafId = requestAnimationFrame(animate);
			}
		};

		const onMove = (e: MouseEvent) => {
			mx = e.clientX;
			my = e.clientY;
			if (!hasPointerPosition) {
				rx = mx;
				ry = my;
				hasPointerPosition = true;
			}
			startAnimation();
		};
		document.addEventListener("mousemove", onMove);

		const animate = () => {
			if (!hasPointerPosition) {
				isAnimating = false;
				rafId = undefined;
				return;
			}

			rx += (mx - rx) * 0.15;
			ry += (my - ry) * 0.15;
			if (cursorRef.current) {
				cursorRef.current.style.left = `${mx}px`;
				cursorRef.current.style.top = `${my}px`;
			}
			if (ringRef.current) {
				ringRef.current.style.left = `${rx}px`;
				ringRef.current.style.top = `${ry}px`;
			}

			if (Math.abs(mx - rx) < 0.1 && Math.abs(my - ry) < 0.1) {
				isAnimating = false;
				rafId = undefined;
				return;
			}

			rafId = requestAnimationFrame(animate);
		};

		const grow = () => {
			if (cursorRef.current) {
				cursorRef.current.style.width = "18px";
				cursorRef.current.style.height = "18px";
			}
			if (ringRef.current) {
				ringRef.current.style.width = "52px";
				ringRef.current.style.height = "52px";
				ringRef.current.style.borderColor = "rgba(0,229,160,0.6)";
			}
		};
		const shrink = () => {
			if (cursorRef.current) {
				cursorRef.current.style.width = "10px";
				cursorRef.current.style.height = "10px";
			}
			if (ringRef.current) {
				ringRef.current.style.width = "36px";
				ringRef.current.style.height = "36px";
				ringRef.current.style.borderColor = "rgba(0,229,160,0.35)";
			}
		};

		const onVisibilityChange = () => {
			if (document.visibilityState === "hidden" && rafId !== undefined) {
				cancelAnimationFrame(rafId);
				rafId = undefined;
				isAnimating = false;
			}
			if (document.visibilityState === "visible" && hasPointerPosition) {
				startAnimation();
			}
		};
		document.addEventListener("visibilitychange", onVisibilityChange);

		const interactables = document.querySelectorAll("a, button");
		interactables.forEach((el) => {
			el.addEventListener("mouseenter", grow);
			el.addEventListener("mouseleave", shrink);
		});

		return () => {
			document.removeEventListener("mousemove", onMove);
			document.removeEventListener("visibilitychange", onVisibilityChange);
			if (rafId !== undefined) {
				cancelAnimationFrame(rafId);
			}
			interactables.forEach((el) => {
				el.removeEventListener("mouseenter", grow);
				el.removeEventListener("mouseleave", shrink);
			});
		};
	}, []);

	// Nav scroll class
	useEffect(() => {
		const nav = document.getElementById("nav");
		const onScroll = () => {
			nav?.classList.toggle("scrolled", window.scrollY > 40);
		};
		window.addEventListener("scroll", onScroll);
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	// Scroll reveal
	useEffect(() => {
		const reveals = document.querySelectorAll(".reveal");
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setTimeout(() => entry.target.classList.add("visible"), 80);
						observer.unobserve(entry.target);
					}
				}
			},
			{ threshold: 0.1 },
		);
		for (const el of reveals) {
			observer.observe(el);
		}
		return () => observer.disconnect();
	}, []);

	const marqueeItems = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];

	return (
		<>
			<div className="grid-bg" />
			<div id="cursor" ref={cursorRef} />
			<div id="cursor-ring" ref={ringRef} />

			{/* NAV */}
			<nav id="nav">
				<div className="nav-logo">kevinpatrickwestropp.com</div>
				<ul className="nav-links">
					<li>
						<a href="#skills">Skills</a>
					</li>
					<li>
						<a href="#experience">Experience</a>
					</li>
					<li>
						<a href="#education">Education</a>
					</li>
					<li>
						<a href="#contact">Contact</a>
					</li>
				</ul>
			</nav>

			{/* HERO */}
			<section id="hero">
				<div className="hero-glow" />
				<div className="hero-glow2" />
				<div className="hero-content">
					<p className="hero-tag">Senior Fullstack Engineer · Denver, CO</p>
					<h1 className="hero-name">
						Kevin
						<br />
						<span className="line2">Patrick</span>
						<span className="accent-word"> West</span>ropp
					</h1>
					<p className="hero-sub">
						Building AI-powered systems and cloud-native products with 8+ years
						of experience across React, Node.js, TypeScript, and AWS.
					</p>
					<div className="hero-cta">
						<a href="mailto:highlanderkev@proton.me" className="btn-primary">
							<span>Get In Touch</span>
							<svg
								width="14"
								height="14"
								viewBox="0 0 14 14"
								fill="none"
								aria-hidden="true"
							>
								<path
									d="M1 7h12M8 3l5 4-5 4"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</a>
						<a
							href="https://linkedin.com/in/kevinwestropp"
							className="btn-ghost"
							target="_blank"
							rel="noopener noreferrer"
						>
							<span>LinkedIn</span>
						</a>
						<a
							href="https://github.com/highlanderkev"
							className="btn-ghost"
							target="_blank"
							rel="noopener noreferrer"
						>
							<span>GitHub</span>
						</a>
					</div>
				</div>
				<div className="scroll-indicator">
					<div className="scroll-line" />
					<span>Scroll</span>
				</div>
			</section>

			{/* STATS */}
			<div className="stats-bar">
				<div className="stat-item reveal">
					<div className="stat-num">8+</div>
					<div className="stat-label">Years Experience</div>
				</div>
				<div className="stat-item reveal">
					<div className="stat-num">9</div>
					<div className="stat-label">Companies</div>
				</div>
				<div className="stat-item reveal">
					<div className="stat-num">M.S.</div>
					<div className="stat-label">Computer Science · DePaul</div>
				</div>
				<div className="stat-item reveal">
					<div className="stat-num">10+</div>
					<div className="stat-label">Certifications</div>
				</div>
			</div>

			{/* MARQUEE */}
			<div className="marquee-wrap">
				<div className="marquee-track">
					{marqueeItems.map((item, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: static duplicated list
						<span key={i} className="marquee-item">
							{item} <span className="sep">✦</span>
						</span>
					))}
				</div>
			</div>

			{/* SKILLS */}
			<section id="skills">
				<p className="section-label reveal">Expertise</p>
				<h2 className="section-title reveal">What I Build</h2>
				<div className="skills-grid reveal">
					{SKILLS.map((skill) => (
						<div key={skill.name} className="skill-card">
							<div className="skill-icon">{skill.icon}</div>
							<div className="skill-name">{skill.name}</div>
							<div className="skill-desc">{skill.desc}</div>
							<div className="skill-tags">
								{skill.tags.map((tag) => (
									<span key={tag} className="tag">
										{tag}
									</span>
								))}
							</div>
						</div>
					))}
				</div>
			</section>

			{/* EXPERIENCE */}
			<section id="experience">
				<div className="exp-layout">
					<div className="exp-sticky">
						<p className="section-label reveal">Career</p>
						<h2 className="section-title reveal">Experience</h2>
						<p className="exp-intro reveal">
							Over 8 years engineering software across fintech, security, data,
							and travel — from startups to Fortune 500 enterprises.
						</p>
					</div>
					<div className="timeline">
						{EXPERIENCE.map((job) => (
							<div key={job.company} className="timeline-item reveal">
								<div className="timeline-date">{job.date}</div>
								<div className="timeline-content">
									<div className="timeline-company">{job.company}</div>
									<div className="timeline-role">{job.role}</div>
									<div className="timeline-desc">{job.desc}</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* EDUCATION */}
			<section id="education">
				<p className="section-label reveal">Credentials</p>
				<h2 className="section-title reveal">Education & Certifications</h2>
				<div className="edu-grid reveal">
					<div className="edu-card">
						<div className="edu-degree">M.S. Computer Science</div>
						<div className="edu-school">DePaul University</div>
						<div className="edu-detail">
							Graduate studies with focus on software engineering, algorithms,
							and applied computing. Strong foundation bridging theory and
							production systems.
						</div>
					</div>
					<div className="edu-card">
						<div className="edu-degree">Certifications</div>
						<div className="edu-school">AI · ML · Full Stack · Cloud</div>
						<div className="cert-list">
							{CERTS.map((cert) => (
								<div key={cert.name} className="cert-item">
									<div className="cert-dot" />
									<div className="cert-name">{cert.name}</div>
									<div className="cert-issuer">{cert.issuer}</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</section>

			{/* CONTACT */}
			<section id="contact">
				<h2 className="contact-headline reveal">
					Let's <span>Build</span>
					<br />
					Something.
				</h2>
				<p className="contact-sub reveal">
					Open to AI-forward engineering roles, technical leadership
					opportunities, and interesting problems.
				</p>
				<div className="contact-links reveal">
					<a href="mailto:highlanderkev@proton.me" className="btn-primary">
						<span>highlanderkev@proton.me</span>
					</a>
				</div>
				<div className="contact-links reveal" style={{ marginTop: "20px" }}>
					<a
						href="https://linkedin.com/in/kevinwestropp"
						className="social-link"
						target="_blank"
						rel="noopener noreferrer"
					>
						LinkedIn
					</a>
					<a
						href="https://github.com/highlanderkev"
						className="social-link"
						target="_blank"
						rel="noopener noreferrer"
					>
						GitHub
					</a>
					<a
						href="https://kevinpatrickwestropp.com"
						className="social-link"
						target="_blank"
						rel="noopener noreferrer"
					>
						Website
					</a>
				</div>
			</section>

			{/* FOOTER */}
			<footer>
				<div className="footer-copy">
					© {new Date().getFullYear()} Kevin Patrick Westropp
				</div>
				<div className="footer-stack">
					Built with <span>React · TypeScript · Vite</span> · Denver, CO
				</div>
			</footer>
		</>
	);
}
